export function formatAmount(currency: string, amount: number): string {
  if (currency.includes('~')) {
    return formatInstrumentQuantity(amount);
  }

  const decimals = currency === 'BTC' ? 8 : 2;
  return Number(amount).toFixed(decimals);
}

/**
 * Decimals the backend stores instrument quantities with (config.exchange.quantityPrecision).
 * Holdings can be fractional (1.254, 100.5), so displays show everything stored; how many
 * decimals a user may ENTER is per instrument (InstrumentPrecisionService).
 */
export const INSTRUMENT_QUANTITY_DISPLAY_PRECISION = 4;

/** Instrument quantity for display: full stored precision, trailing zeros dropped (10, 1.254, 100.5). */
export function formatInstrumentQuantity(
  amount: number | string | null | undefined
): string {
  const value = Number(amount);
  if (amount === null || amount === undefined || amount === '' || !isFinite(value)) {
    return '-';
  }
  return String(Number(value.toFixed(INSTRUMENT_QUANTITY_DISPLAY_PRECISION)));
}

/**
 * Cuts a quantity down to `decimals` places (toward zero, never more units than entered), e.g.
 * when the operator switches to an instrument with fewer Quantity Decimal Places. Empty stays empty.
 */
export function truncateQuantity<T extends number | null | undefined>(
  value: T,
  decimals: number
): T | number {
  if (value === null || value === undefined || !isFinite(Number(value))) return value;
  const scale = Math.pow(10, decimals);
  return Math.trunc(Number((Number(value) * scale).toFixed(8))) / scale;
}

/**
 * Exact "is `value` a multiple of `step`" at the stored quantity scale. Float modulo is
 * unusable for fractional steps: 0.3 % 0.1 is 0.0999..., not 0. A step <= 0 allows anything.
 */
export function isMultipleOfStep(value: number, step: number | null | undefined): boolean {
  if (!step || step <= 0) return true;
  const scale = Math.pow(10, INSTRUMENT_QUANTITY_DISPLAY_PRECISION);
  return Math.round(value * scale) % Math.round(step * scale) === 0;
}

/**
 * `${tickerId}~${sourceId}` (e.g. AAPL~NSDQ) is the Balance-row key for a securities
 * position — there is no separate holdings collection, so `~` is the discriminator.
 */
export function isInstrumentSymbol(currency: string | null | undefined): boolean {
  return !!currency?.includes('~');
}

/**
 * Truncate (never round) to `precision` decimals, matching the backend.
 * Scaling first is float-unsafe — `Math.trunc(0.7 * 10)` is 6 — so the scaled
 * value is snapped to an epsilon before truncating.
 */
export function truncateTo(value: number, precision: number): number {
  const factor = Math.pow(10, precision);
  const scaled = Number((Math.abs(value) * factor).toFixed(6));
  return (Math.sign(value) || 1) * (Math.trunc(scaled) / factor);
}

export function formatPnl(amount: number): string {
  const sign = amount < 0 ? '-' : '+';
  return `${sign}$${Math.abs(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
